import { CButton, CInput, CPagination } from '@coreui/react';
import React, { useCallback, useEffect, useState } from 'react'
import { GetMaterialAssignList, PostMaterialAssign } from 'src/request/Materials';
import useLoading from 'src/utils/useLoading';
import { useSelector } from 'react-redux';
import useCustomPagination from 'src/utils/useCustomPagination';
import { debounce } from 'lodash';
import { preventInvalidKeys, blurOnWheel, preventInvalidPaste } from 'src/reusable/inputSettings';
const Swal = require('sweetalert2');

const MaterialAssign = ({ projectID, companyID }) => {
  const { InlineLoading } = useLoading();
  const [materialList, setMaterialList] = useState([]);
  const [materialInputs, setMaterialInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const userID = localStorage.getItem('user_id');
  const token = useSelector((state) => state.auth.token);
  const { currentPage, pageSize, totalPages, searchTerm, updatePagination } = useCustomPagination();

  const fetchMaterialList = async () => {
    try {
      setLoading(true);
      const res = await GetMaterialAssignList({
        project_id: projectID,
        page: currentPage,
        per_page: pageSize,
        search: searchTerm,
      });

      if (res.status === 200) {
        setMaterialList(res?.data?.data_list || []);
        let totalPages_ = Math.max(res?.data?.total_pages ?? 0, 1);
        if (totalPages !== totalPages_) {
          updatePagination({ newTotalPages: totalPages_ });
        }
      }
    } catch (error) {
      console.error('Something went wrong please try again.', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, value) => {
    setMaterialInputs(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const handleMaterialRequest = async () => {
    try {
      const purchase_request = materialList?.filter((item) => materialInputs[item.pm_id])
        .map((item, _) => ({
          material_id: item.material_id,
          sow_id: item.sow_id,
          psow_id: item.psow_id,
          total_order: parseFloat(materialInputs[item.pm_id]),
          unit: item.Unit,
          pm_id: item.pm_id,
          notes: ""
        }));

      if (purchase_request.length === 0) {
        Swal.fire({ icon: 'info', title: 'Info', text: 'Please enter quantity for at least one material' });
        return;
      }

      const payload = {
        user_id: userID,
        purchase_request: purchase_request,
        project_id: projectID,
        company_id: companyID,
        token: token,
      };

      const response = await PostMaterialAssign(payload);

      if (response.status === 200) {
        setMaterialInputs({});
        await fetchMaterialList();
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Material request created successfully'
        });
      }
    } catch (error) {
      console.error('Something went wrong. Please try again', error);
      Swal.fire({
        icon: 'error',
        title: 'Oops',
        text: 'Something went wrong. Please try again'
      });
    }
  };

  const handleSearch = debounce((e) => {
    updatePagination({ newSearchTerm: e, newCurrentPage: 1 });
  }, 500);

  const debounceSetCurrentPage = useCallback(
    debounce((page) => {
      if (page > 0) updatePagination({ newCurrentPage: page });
    }, 400),
    [],);

  useEffect(() => {
    fetchMaterialList();
  }, [searchTerm, currentPage]);



  const getProgressBarClass = (progress) => {
    if (progress > 100) return 'bg-[#b42a19]';
    if (progress >= 90) return 'bg-[#b6451d]';
    if (progress >= 75) return 'bg-[#f2e442]';
    if (progress >= 50) return 'bg-[#3a7d21]';
    return 'bg-[#3a7d21]';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Search
          </label>
          <CInput
            type="search"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Type material name to search"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <div>
          <CButton
            block
            color="aimhi-primary"
            className="mt-4"
            size="md"
            onClick={handleMaterialRequest}
          >
            Create request
          </CButton>
        </div>
      </div>

      {loading ? (
        <>
          <InlineLoading />
        </>
      ) : (
        <>
          <div className="space-y-3 min-h-60">
            {materialList.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                No materials found
              </div>
            ) : (
              materialList.map((item, index) => {
                const progress = parseInt(item.TotalQuantity) === 0 ? 0 : (parseInt(item.TotalOrder) / parseInt(item.TotalQuantity)) * 100;
                const progressBarClass = getProgressBarClass(progress);

                return (
                  <div key={index} className="grid items-start grid-cols-1 gap-2 md:grid-cols-12">
                    <div className="my-0 md:col-span-4">
                      <span className="text-sm">{item.MaterialName}</span>
                    </div>

                    <div className="md:col-span-6">
                      <div className="relative h-5 overflow-hidden bg-gray-100 rounded-full">
                        <div className={`absolute h-full transition-all duration-300 ${progressBarClass}`} style={{ width: `${Math.min(progress, 100)}%` }} />

                        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs font-bold whitespace-nowrap">
                          <div className={`absolute left-1/2 -translate-x-1/2  ${progress <= 50 && 'text-aimhi-textlight-neutral'}  ${progress > 50 && progress < 75 && 'text-white'}  ${progress >= 75 && progress < 100 && 'text-aimhi-textlight-neutral'}  ${progress >= 100 && 'text-white'} `} >
                            {item.TotalOrder}
                          </div>

                          <div className={`ml-auto ${progress <= 90 ? 'text-aimhi-textlight-neutral' : 'text-white'}`} >
                            {item.TotalOrder}/{item.TotalQuantity} {item.Unit}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <CInput
                        type="number"
                        value={materialInputs[item.pm_id] || ''}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        size="sm"
                        placeholder="0"
                        onChange={(e) => handleInputChange(item.pm_id, e.target.value)}
                        onKeyDown={preventInvalidKeys}
                        onWheel={blurOnWheel}
                        onPaste={preventInvalidPaste}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className='my-4'>
            <CPagination activePage={currentPage} onActivePageChange={debounceSetCurrentPage} pages={totalPages} doubleArrows={true} align="center" />
          </div>
        </>
      )}
    </div>
  );
};

export default MaterialAssign;